import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

/**
 * Пикер сотрудника 1С ЗУП. По образу AdUserPicker, но проще:
 *   • Источник — локальный кэш `onec_employees` (POST /api/onec/employees/sync —
 *     pull из ЗУП по нажатию).
 *   • value: { id, code, name } | { id, syncState: 'missing' } | null
 *   • onChange: ({ id, code, name } | null)
 */
export default function OnecEmployeePicker({ value, onChange, defaultName, disabled }) {
  const can = useCan();
  const canSyncFromZup = can('settings.1c_zup', 'write');

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    setSearch(defaultName || '');
  }, [open, defaultName]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true); setError(null);
      try {
        const data = await api.get(
          `/api/onec/employees?search=${encodeURIComponent(search)}&limit=100`
        );
        setResults(data.rows || []);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить');
        setResults([]);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [search, open]);

  const onSyncFromZup = async () => {
    if (syncing) return;
    setSyncing(true); setError(null);
    try {
      await api.post('/api/onec/employees/sync', {});
      // Pull асинхронный — даём ему пару секунд и обновляем поиск.
      setTimeout(async () => {
        try {
          const data = await api.get(
            `/api/onec/employees?search=${encodeURIComponent(search)}&limit=100`
          );
          setResults(data.rows || []);
        } catch {}
        setSyncing(false);
      }, 1500);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось запустить синк');
      setSyncing(false);
    }
  };

  const isMissing = !!(value && value.syncState === 'missing');
  const display = value && (value.name || (isMissing ? '— сотрудник удалён из ЗУП —' : '#' + value.id));

  return (
    <div className="ad-picker" ref={ref}>
      <div className="ad-picker__row">
        <button
          type="button"
          className="ad-picker__current"
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
        >
          {value ? (
            <span className="ad-picker__value">
              <span className="ad-picker__name" style={isMissing ? { color: 'var(--red-fg)' } : undefined}>
                {display}
              </span>
              {value.code && <span className="mono ad-picker__upn">{value.code}</span>}
            </span>
          ) : <span className="ad-picker__empty">Выберите сотрудника 1С ЗУП…</span>}
          <Icon name="chevronDown" size={10} />
        </button>
        {value && !disabled && (
          <button
            type="button"
            className="ad-picker__clear"
            onClick={() => onChange(null)}
            title="Отвязать"
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>

      {open && (
        <div className="ad-picker__menu">
          <div style={{ display: 'flex', gap: 6, padding: 6 }}>
            <input
              className="ad-picker__search"
              placeholder="Поиск: ФИО или код 1С"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{ flex: 1 }}
            />
            {canSyncFromZup && (
              <button
                type="button"
                className="btn btn--sm"
                onClick={onSyncFromZup}
                disabled={syncing}
                title="Запросить актуальный список сотрудников из 1С ЗУП"
                style={{ padding: '0 8px', height: 28 }}
              >
                <Icon name={syncing ? 'clock' : 'refresh'} size={12} />
                {syncing ? '…' : 'Обновить'}
              </button>
            )}
          </div>
          {error && <div className="ad-picker__error">{error}</div>}
          <div className="ad-picker__list">
            {loading && <div className="ad-picker__hint">Поиск в кэше…</div>}
            {!loading && !error && results.map((r) => (
              <button
                key={r.id}
                type="button"
                className="ad-picker__item"
                onClick={() => { onChange(r); setOpen(false); }}
              >
                <span className="ad-picker__item-name">{r.name}</span>
                <span className="ad-picker__item-meta">
                  <span className="mono">{r.code}</span>
                </span>
              </button>
            ))}
            {!loading && !error && results.length === 0 && (
              <div className="ad-picker__hint">
                {search
                  ? 'Ничего не найдено. Возможно, сотрудника ещё не подтянули из ЗУП — нажмите «Обновить».'
                  : 'Начните вводить ФИО или код'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
